import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BridgeMessages } from '../../../nextray/Core/Bridge';
import { OsteotomyCut } from '../../../nextray/States/State';
import { ToastService } from '../../core';
import { BaseComponent, ConfirmationComponent } from '../../shared';


/**
* Component of first vertical sidebar tool menu to handle correction.
*/
@Component({
	selector: 'correction-menu',
	templateUrl: './correction-menu.component.html',
	imports: [
		FormsModule,
		ReactiveFormsModule
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class CorrectionMenuComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly modalSrv = inject(NgbModal);
	private readonly toastSrv = inject(ToastService);

	cutType = OsteotomyCut;
	cutForm?: FormGroup;


	ngOnInit(): void {
		this.canvasSrv.addEventListener('onResult', this.cutError);
		this.canvasSrv.addEventListener('onResult', this.cutInit);
		this.initCutForm();
	}

	private openWarningModal(message: string, confirmFunction: Function, cancelFunction?: Function): void {
		if (!this.modalSrv.hasOpenModals()) {
			const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().TOOLS_MENU_COMPONENT_WARNING_MODAL_TITLE,
				message: message
			};
			modalRef.result.then(() => confirmFunction.call(this), () => cancelFunction ? cancelFunction.call(this) : null).catch(error => false);
		}
	}

	/**
	* Display Correction menu tools.
	*/
	enableCorrection(): void {
		if (this.isDeformityDone) {
			if (this.stateType === this.stateTypeEnum.deformityAnalysis) {
				this.goToEOC();
			} else {
				const message: string = this.labels().TOOLS_MENU_COMPONENT_CORRECTION_MODAL_MESSAGE;
				this.openWarningModal(message, this.goToEOC);
			}
		}
	}

	private goToEOC(): void {
		this.canvasSrv.closeDrawer();
		this.canvasSrv.dispatch('switchToEOC', null);
		this.canvasSrv.saveState();
		this.initCutForm();
	}

	/**
	* Start osteotomy site workflow.
	*/
	openOsteotomyWorkflow(): void {
		if (!this.isEocCut) {
			if (this.osteotomyCut) {
				const message: string = this.labels().TOOLS_MENU_COMPONENT_OSTEOTOMY_MODAL_MESSAGE;
				this.openWarningModal(message, this.startApexSelection);
			} else {
				this.startApexSelection();
			}
		}
	}

	private startApexSelection(): void {
		this.canvasSrv.dispatch('startSelectingApex');
	}

	private initCutForm(): void {
		this.cutForm = new FormGroup({
			'cutType': new FormControl({ value: this.osteotomyCut, disabled: !this.selectedApex }),
			'cut': new FormControl({ value: this.isEocCut, disabled: !this.osteotomyCut })
		});
	}

	/**
	* Event listener for cut error.
	*/
	cutError = (event: { args: keyof BridgeMessages }) => {
		if (event.args == 'cannotCut') {
			this.toastSrv.showWarning(this.labels().TOOLS_MENU_COMPONENT_CUT_ERROR);
			this.cutForm?.controls['cut'].setValue(this.isEocCut);
		}
	};

	/**
	* Event listener for cut initialization.
	*/
	cutInit = (event: { args: keyof BridgeMessages }) => {
		if (event.args == 'restore' || event.args == 'historyChanged') {
			this.initCutForm();
		}
	};

	/**
	* Set osteotomy opening cut.
	*/
	setOpening(): void {
		if (this.isEocCut && this.osteotomyCut === this.cutType.closing) {
			const message: string = this.labels().TOOLS_MENU_COMPONENT_CUT_MODAL_MESSAGE;
			this.openWarningModal(message, this.setCutOpeningAndReset, this.setCutClosing);
		} else {
			this.setCutOpening();
		}
	}

	private setCutOpening(): void {
		this.canvasSrv.dispatch('osteotomyCutOpening');
		this.cutForm?.controls['cutType'].setValue(this.cutType.opening);
		this.cutForm?.controls['cut'].enable();
	}

	private setCutOpeningAndReset(): void {
		this.setCutOpening();
		if (this.isEocCut) {
			this.setCut();
			this.cutForm?.controls['cut'].setValue(this.isEocCut);
		}
	}

	/**
	* Set osteotomy closing cut.
	*/
	setClosing(): void {
		if (this.isEocCut && this.osteotomyCut === this.cutType.opening) {
			const message: string = this.labels().TOOLS_MENU_COMPONENT_CUT_MODAL_MESSAGE;
			this.openWarningModal(message, this.setCutClosingAndReset, this.setCutOpening);
		} else {
			this.setCutClosing();
		}
	}

	private setCutClosing(): void {
		this.canvasSrv.dispatch('osteotomyCutClosing');
		this.cutForm?.controls['cutType'].setValue(this.cutType.closing);
		this.cutForm?.controls['cut'].enable();
	}

	private setCutClosingAndReset(): void {
		this.setCutClosing();
		if (this.isEocCut) {
			this.setCut();
			this.cutForm?.controls['cut'].setValue(this.isEocCut);
		}
	}

	/**
	* Set cut.
	*/
	setCut(): void {
		const val = !this.isEocCut;
		this.canvasSrv.dispatch('cutEoC', val);
	}

	/**
	* Reset cut.
	*/
	resetCut(): void {
		if (this.isEocCut) {
			this.setCut();
			this.setCut();
		}
	}


	ngOnDestroy(): void {
		this.canvasSrv.removeEventListener('onResult', this.cutError);
		this.canvasSrv.removeEventListener('onResult', this.cutInit);
	}

}